// src/pages/Amostras/AmostraMockModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Typography,
    Paper,
    Divider,
    IconButton,
    CircularProgress,
    Alert,
    InputAdornment,
    Autocomplete,
    Chip,
    useTheme,
    useMediaQuery,
    Box,
    Avatar,
    Tooltip,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Slider,
    Collapse,
} from '@mui/material';
import * as Lucide from 'lucide-react';

import { getTowers, searchTowers } from '../../infra/api/Torres';
import { getExperimentosPagedRaw } from '../../infra/api/ExperimentoApi';
import { useDebounce } from '../../hooks/useDebounce';
import { useBootstrap } from '../../context/BootstrapContext';

const normalizeId = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
        const m = val.match(/ObjectId\("([^"]+)"\)/);
        if (m) return m[1];
        try {
            const parsed = JSON.parse(val);
            if (parsed?.$oid) return String(parsed.$oid);
        } catch (e) {}
        return val;
    }
    if (typeof val === 'object' && val?.$oid) return String(val.$oid);
    return String(val);
};

const normalizeProtocoloAnaliseRef = (ref) => {
    if (!ref || typeof ref !== 'object') return null;
    const protocoloId = normalizeId(ref.protocoloId);
    const analiseId = normalizeId(ref.analiseId);
    const protocoloNome = String(ref.protocoloNome || '').trim();
    const analiseNome = String(ref.analiseNome || '').trim();
    if (!protocoloId && !analiseId && !protocoloNome && !analiseNome) {
        return null;
    }
    return {
        ...(protocoloId ? { protocoloId } : {}),
        ...(protocoloNome ? { protocoloNome } : {}),
        ...(analiseId ? { analiseId } : {}),
        ...(analiseNome ? { analiseNome } : {}),
    };
};

const upper = (v) =>
    String(v || '')
        .toUpperCase()
        .trim();

const statusChip = (status) => {
    const s = upper(status);
    if (s === 'PENDENTE') return { label: 'Pendente', color: 'warning' };
    if (s === 'EM_PROCESSAMENTO')
        return { label: 'Em processamento', color: 'info' };
    if (s === 'ENCERRADA') return { label: 'Encerrada', color: 'success' };
    if (s === 'COMPLETED') return { label: 'Concluído', color: 'success' };
    return { label: s || '—', color: 'default' };
};

const toDateOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    const s = String(v).trim();
    if (!s) return null;
    const n = Number(s);
    if (!Number.isNaN(n) && Number.isFinite(n)) {
        const ms = n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (v) => {
    const d = toDateOrNull(v);
    if (!d) return '—';
    return d.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const fetchLocalWeather = async (lat, lon) => {
    try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Falha ao buscar clima');
        const data = await res.json();
        return data.current_weather;
    } catch (e) {
        console.error('Erro ao buscar clima (Front):', e);
        return null;
    }
};

const fetchAddressReverse = async (lat, lon) => {
    try {
        const urlBDC = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=pt`;
        const res = await fetch(urlBDC);
        if (res.ok) {
            const data = await res.json();
            const city = data.city || data.locality || '';
            const state = data.principalSubdivision || '';
            return {
                city,
                state,
                formatted: [city, state].filter(Boolean).join(' - '),
            };
        }
    } catch (e) {}

    return { city: '', state: '', formatted: 'Localização GPS' };
};

const searchAddressForward = async (query) => {
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&limit=1`;
        const res = await fetch(url, {
            headers: { 'Accept-Language': 'pt-BR' },
        });
        if (res.ok) {
            const data = await res.json();
            if (data && data.length > 0) {
                return {
                    latitude: parseFloat(data[0].lat),
                    longitude: parseFloat(data[0].lon),
                };
            }
        }
    } catch (e) {
        console.warn('Nominatim falhou', e);
    }

    try {
        const urlPhoton = `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=1`;
        const res = await fetch(urlPhoton);
        if (res.ok) {
            const data = await res.json();
            if (data.features && data.features.length > 0) {
                const [lon, lat] = data.features[0].geometry.coordinates;
                return { latitude: lat, longitude: lon };
            }
        }
    } catch (e) {
        console.error('Todas APIs falharam', e);
    }

    return null;
};

const searchAddressForwardList = async (query) => {
    try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`;
        const res = await fetch(url, {
            headers: { 'Accept-Language': 'pt-BR' },
        });
        if (!res.ok) throw new Error('Falha na busca');
        const data = await res.json();
        return data.map((item) => {
            const addr = item.address || {};
            const city =
                addr.city ||
                addr.town ||
                addr.village ||
                addr.municipality ||
                '';
            const state = addr.state || addr.region || '';
            const countryCode = addr.country_code
                ? addr.country_code.toUpperCase()
                : '';
            let label = [item.display_name].filter(Boolean).join(', ');

            return {
                lat: parseFloat(item.lat),
                lon: parseFloat(item.lon),
                displayName: item.display_name,
                label,
                city,
                state,
                countryCode,
            };
        });
    } catch (e) {
        return [];
    }
};

const SearchLocationDialog = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setQuery('');
            setResults([]);
        }
    }, [open]);

    const handleSearch = async () => {
        if (!query.trim()) return;
        setLoading(true);
        const res = await searchAddressForwardList(query);
        setResults(res);
        setLoading(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    position: { xs: 'absolute', sm: 'relative' },
                    top: { xs: 16, sm: 'auto' },
                    m: 2,
                    width: { xs: 'calc(100% - 32px)', sm: '100%' },
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle
                sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1.5,
                }}
            >
                <Lucide.Search size={18} />
                <Typography
                    variant="subtitle1"
                    sx={{ flexGrow: 1, fontWeight: 600 }}
                >
                    Buscar Endereço
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'white' }}
                >
                    <Lucide.X size={18} />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Digite cidade..."
                        placeholder="Ex: Santa Maria"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearch();
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleSearch}
                                        disabled={loading || !query.trim()}
                                        edge="end"
                                        size="small"
                                    >
                                        {loading ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Lucide.Search />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {results.length > 0 && (
                        <List
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                maxHeight: '250px',
                                overflow: 'auto',
                                bgcolor: 'background.paper',
                            }}
                        >
                            {results.map((item, idx) => (
                                <ListItemButton
                                    key={idx}
                                    onClick={() => onSelect(item)}
                                    sx={{ py: 1 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Lucide.MapPin
                                            color={theme.palette.error.main}
                                            size={18}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                            >
                                                {item.city}{' '}
                                                {item.state
                                                    ? `- ${item.state}`
                                                    : ''}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                noWrap
                                            >
                                                {item.displayName}
                                            </Typography>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

const DilutionInput = ({ value, onChange, readOnly }) => {
    const theme = useTheme();
    const [isEditing, setIsEditing] = useState(false);
    const [sliderVal, setSliderVal] = useState(Number(value) || 0);

    useEffect(() => {
        if (!isEditing) setSliderVal(Number(value) || 0);
    }, [value, isEditing]);

    const handleChangeCommitted = (_, newVal) => {
        onChange(newVal);
        setIsEditing(false);
    };

    const displayValue =
        Number(value) > 0 ? `Diluição: 10⁻${value}` : 'Sem diluição';
    const sliderDisplay = sliderVal > 0 ? `10⁻${sliderVal}` : 'Sem diluição';
    const chipColor = Number(value) > 0 ? 'primary' : 'default';

    return (
        <Box sx={{ width: '100%' }}>
            <Chip
                icon={<Lucide.Pencil size={16} />}
                label={displayValue}
                size="medium"
                variant="outlined"
                color={chipColor}
                onClick={() => !readOnly && setIsEditing((prev) => !prev)}
                disabled={readOnly}
                sx={{
                    borderRadius: 1,
                    height: 40,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    width: '100%',
                    justifyContent: 'space-between',
                    px: 1,
                    bgcolor: isEditing
                        ? chipColor === 'primary'
                            ? 'primary.50'
                            : 'action.selected'
                        : 'transparent',
                    borderWidth: 1,
                    borderColor:
                        chipColor === 'primary' ? 'primary.main' : 'divider',
                }}
            />
            <Collapse in={isEditing} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 1,
                        mt: 1,
                        bgcolor: 'background.default',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: theme.shadows[1],
                        position: 'relative',
                        zIndex: 10,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                    >
                        <Typography variant="caption" fontWeight={700}>
                            Ajustar: {sliderDisplay}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setIsEditing(false)}
                            sx={{ p: 0.5 }}
                        >
                            <Lucide.X size={14} />
                        </IconButton>
                    </Stack>
                    <Slider
                        size="small"
                        value={sliderVal}
                        onChange={(_, v) => setSliderVal(v)}
                        onChangeCommitted={handleChangeCommitted}
                        min={0}
                        max={6}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        disabled={readOnly}
                    />
                </Box>
            </Collapse>
        </Box>
    );
};

const AmostraMockModal = ({ open, onClose, onSubmit }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { bootstrap } = useBootstrap();

    const protocolos = useMemo(
        () => bootstrap?.protocolos || bootstrap?.protocols || [],
        [bootstrap]
    );
    const [saving, setSaving] = useState(false);

    const [identificacao, setIdentificacao] = useState('');
    const [protocoloId, setProtocoloId] = useState('');
    const [analiseId, setAnaliseId] = useState('');

    const [location, setLocation] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState('');
    const [ambientTemperature, setAmbientTemperature] = useState('');
    const [displayAddress, setDisplayAddress] = useState('');
    const [isAutoLocation, setIsAutoLocation] = useState(false);
    const [openSearchModal, setOpenSearchModal] = useState(false);

    const protocoloSelecionado = useMemo(() => {
        const pid = normalizeId(protocoloId);
        return (
            protocolos.find((p) => normalizeId(p?.id || p?._id) === pid) || null
        );
    }, [protocolos, protocoloId]);

    const analisesDoProtocolo = useMemo(() => {
        const list =
            protocoloSelecionado?.analises ||
            protocoloSelecionado?.analysis ||
            [];
        return Array.isArray(list) ? list : [];
    }, [protocoloSelecionado]);

    const etapaTorresLiberada = useMemo(() => {
        return !!normalizeId(protocoloId) && !!normalizeId(analiseId);
    }, [protocoloId, analiseId]);

    const [towerQuery, setTowerQuery] = useState('');
    const debouncedTowerQuery = useDebounce(towerQuery, 400);
    const [towerLoading, setTowerLoading] = useState(false);
    const [towerOptions, setTowerOptions] = useState([]);

    const [items, setItems] = useState([]);

    const selectedTowerIds = useMemo(
        () =>
            new Set(
                items.map((x) => normalizeId(x?.tower?.id || x?.tower?._id))
            ),
        [items]
    );

    const [ruleError, setRuleError] = useState('');

    const updateCoordsAndWeather = async (lat, lon) => {
        setLocationLoading(true);
        setLocation({
            latitude: lat,
            longitude: lon,
            accuracy: 100,
            capturedAt: new Date().toISOString(),
        });
        const w = await fetchLocalWeather(lat, lon);
        if (w) {
            if (w.temperature !== undefined)
                setAmbientTemperature(String(w.temperature));
        }
        setLocationLoading(false);
    };

    const handleSelectManualLocation = (item) => {
        setOpenSearchModal(false);
        if (item && item.lat && item.lon) {
            setDisplayAddress(`${item.city} - ${item.state}`);
            setIsAutoLocation(false);
            updateCoordsAndWeather(item.lat, item.lon);
        }
    };

    const requestLocation = () => {
        if (!navigator.geolocation) return;
        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const lat = pos.coords.latitude;
                const lon = pos.coords.longitude;
                const rev = await fetchAddressReverse(lat, lon);
                setDisplayAddress(rev.formatted);
                setIsAutoLocation(false);
                await updateCoordsAndWeather(lat, lon);
            },
            () => {
                setLocationLoading(false);
                setLocationError('Falha ao obter GPS');
            },
            { enableHighAccuracy: true }
        );
    };

    const clearLocation = () => {
        setLocation(null);
        setAmbientTemperature('');
        setDisplayAddress('');
        setIsAutoLocation(false);
    };

    useEffect(() => {
        if (!open) return;
        setSaving(false);
        setIdentificacao(`MOCK-${Date.now()}`);
        setProtocoloId('');
        setAnaliseId('');
        setTowerQuery('');
        setTowerOptions([]);
        setItems([]);
        setRuleError('');

        if (bootstrap?.client) {
            const c = bootstrap.client;
            setDisplayAddress(`${c.city} - ${c.state}`);
            const queryParts = [
                c.street,
                c.number,
                c.neighborhood,
                c.city,
                c.state,
                c.zipCode,
            ].filter(Boolean);

            if (queryParts.length > 0) {
                const fullQuery = queryParts.join(', ');
                setLocationLoading(true);
                searchAddressForward(fullQuery).then((coords) => {
                    if (coords) {
                        setIsAutoLocation(true);
                        updateCoordsAndWeather(
                            coords.latitude,
                            coords.longitude
                        );
                    } else {
                        setIsAutoLocation(false);
                        setLocationLoading(false);
                    }
                });
            } else {
                setLocation(null);
                setIsAutoLocation(false);
            }
        } else {
            clearLocation();
        }
    }, [open, bootstrap]);

    useEffect(() => {
        if (!open) return;
        if (!etapaTorresLiberada) {
            setTowerOptions([]);
            return;
        }

        const run = async () => {
            setTowerLoading(true);
            try {
                const pid = normalizeId(protocoloId);
                const aid = normalizeId(analiseId);

                if (debouncedTowerQuery?.trim()) {
                    const results = await searchTowers(
                        debouncedTowerQuery.trim(),
                        { protocoloId: pid, analiseId: aid }
                    );
                    setTowerOptions(Array.isArray(results) ? results : []);
                } else {
                    const page = await getTowers(0, 50, {
                        protocoloId: pid,
                        analiseId: aid,
                    });
                    setTowerOptions(page?.content || []);
                }
            } catch {
                setTowerOptions([]);
            } finally {
                setTowerLoading(false);
            }
        };
        run();
    }, [
        open,
        debouncedTowerQuery,
        etapaTorresLiberada,
        protocoloId,
        analiseId,
    ]);

    const handleChangeProtocolo = (newId) => {
        const id = normalizeId(newId);
        setProtocoloId(id);
        setAnaliseId('');
        setTowerQuery('');
        setTowerOptions([]);
        setItems([]);
        setRuleError('');
    };

    const handleChangeAnalise = (newId) => {
        const id = normalizeId(newId);
        setAnaliseId(id);
        setTowerQuery('');
        setTowerOptions([]);
        setItems([]);
        setRuleError('');
    };

    const loadExperimentosForTower = async (tower) => {
        const serialNumber = String(tower?.numeroSerie || '').trim();
        if (!serialNumber) return [];
        const protocolId = normalizeId(protocoloId);
        const pageRes = await getExperimentosPagedRaw({
            serialNumber,
            protocolId: protocolId || undefined,
            ativo: true,
            page: 0,
            size: 50,
            sort: 'startDate',
            dir: 'desc',
        });
        return Array.isArray(pageRes?.items) ? pageRes.items : [];
    };

    const addTower = async (tower) => {
        if (!etapaTorresLiberada) return;
        const id = normalizeId(tower?.id || tower?._id);
        if (!id) return;
        if (selectedTowerIds.has(id)) return;

        setRuleError('');
        setItems((prev) => [
            ...prev,
            {
                tower,
                diluicao: '',
                experimento: null,
                expOptions: [],
                expLoading: true,
            },
        ]);

        try {
            const exps = await loadExperimentosForTower(tower);
            setItems((prev) =>
                prev.map((x) => {
                    const xid = normalizeId(x?.tower?.id || x?.tower?._id);
                    if (xid !== id) return x;
                    return { ...x, expOptions: exps, expLoading: false };
                })
            );
        } catch (e) {
            setItems((prev) =>
                prev.map((x) => {
                    const xid = normalizeId(x?.tower?.id || x?.tower?._id);
                    if (xid !== id) return x;
                    return { ...x, expOptions: [], expLoading: false };
                })
            );
        }
    };

    const removeTower = (towerId) => {
        setItems((prev) =>
            prev.filter(
                (x) => normalizeId(x?.tower?.id || x?.tower?._id) !== towerId
            )
        );
    };

    const updateDiluicao = (towerId, value) => {
        setItems((prev) =>
            prev.map((x) => {
                const id = normalizeId(x?.tower?.id || x?.tower?._id);
                if (id !== towerId) return x;
                return { ...x, diluicao: value };
            })
        );
    };

    const updateExperimento = (towerId, exp) => {
        setItems((prev) =>
            prev.map((x) => {
                const id = normalizeId(x?.tower?.id || x?.tower?._id);
                if (id !== towerId) return x;
                return { ...x, experimento: exp || null };
            })
        );
    };

    const canSubmit = useMemo(() => {
        if (!identificacao.trim()) return false;
        if (!normalizeId(protocoloId)) return false;
        if (!normalizeId(analiseId)) return false;
        if (!items.length) return false;
        if (items.some((x) => !normalizeId(x?.tower?.id || x?.tower?._id)))
            return false;
        if (items.some((x) => !normalizeId(x?.experimento?.id))) return false;
        if (saving) return false;
        if (!location) return false;
        return true;
    }, [identificacao, protocoloId, analiseId, items, saving, location]);

    const handleSubmit = async () => {
        if (!canSubmit) return;

        let finalSampleTemp = null;
        const rawTemp = String(ambientTemperature).trim();
        if (rawTemp !== '') {
            const sanitized = rawTemp.replace(',', '.');
            const parsed = parseFloat(sanitized);
            if (!isNaN(parsed)) {
                finalSampleTemp = parsed;
            }
        }

        const payload = {
            identificacao: identificacao.trim(),
            protocolosAnalises: (() => {
                const pid = normalizeId(protocoloId);
                const aid = normalizeId(analiseId);
                const protocoloSelecionadoAtual =
                    protocolos.find(
                        (item) => normalizeId(item?.id || item?._id) === pid
                    ) || null;
                const analiseSelecionadaAtual =
                    analisesDoProtocolo.find(
                        (item) => normalizeId(item?.id || item?._id) === aid
                    ) || null;
                return [
                    normalizeProtocoloAnaliseRef({
                        protocoloId: pid || undefined,
                        analiseId: aid || undefined,
                        protocoloNome:
                            protocoloSelecionadoAtual?.nome ||
                            protocoloSelecionadoAtual?.name ||
                            undefined,
                        analiseNome:
                            analiseSelecionadaAtual?.nome ||
                            analiseSelecionadaAtual?.name ||
                            undefined,
                    }),
                ].filter(Boolean);
            })(),
            localizacao: location || undefined,
            temperaturaAmostra: finalSampleTemp,
            itens: items.map((x) => {
                const torreId = normalizeId(x?.tower?.id || x?.tower?._id);
                const experimentoId = normalizeId(x?.experimento?.id);
                const obj = { torreId, experimentoId };
                const d = String(x?.diluicao ?? '').trim();
                if (d !== '')
                    obj.diluicao = Number.isNaN(Number(d)) ? d : Number(d);
                return obj;
            }),
        };
        setSaving(true);
        try {
            await onSubmit?.(payload);
        } finally {
            setSaving(false);
        }
    };

    const icon = <Lucide.Square size={18} />;
    const checkedIcon = <Lucide.SquareCheck size={18} />;

    return (
        <>
            <Dialog
                open={open}
                onClose={saving ? undefined : onClose}
                fullWidth
                maxWidth="md"
                fullScreen={isMobile}
                PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
            >
                <DialogTitle
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: isMobile ? 1.5 : 2,
                    }}
                >
                    <Lucide.FlaskConical />
                    <Typography
                        variant={isMobile ? 'subtitle1' : 'h6'}
                        component="div"
                        sx={{
                            flexGrow: 1,
                            fontWeight: 600,
                            fontSize: isMobile ? '1rem' : '1.25rem',
                        }}
                    >
                        Nova Análise Mock
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: 'inherit' }}
                        disabled={saving}
                    >
                        <Lucide.X />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{ bgcolor: 'background.default', p: isMobile ? 2 : 3 }}
                >
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1,
                                }}
                            >
                                <Lucide.Tag size={18} /> Identificação
                            </Typography>
                            <TextField
                                fullWidth
                                size="small"
                                label="Identificação da Análise"
                                value={identificacao}
                                onChange={(e) =>
                                    setIdentificacao(e.target.value)
                                }
                                required
                                disabled={saving}
                                placeholder="Ex: MOCK-TESTE-01"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lucide.QrCode color={theme.palette.action.active} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <Lucide.Shapes size={18} /> Contexto da
                                Análise
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={saving}
                                    >
                                        <InputLabel>Protocolo</InputLabel>
                                        <Select
                                            label="Protocolo"
                                            value={protocoloId}
                                            onChange={(e) =>
                                                handleChangeProtocolo(
                                                    e.target.value
                                                )
                                            }
                                            startAdornment={
                                                <InputAdornment
                                                    position="start"
                                                    sx={{ mr: 1 }}
                                                >
                                                    <Lucide.ClipboardList
                                                        size={18}
                                                        color={theme.palette.action.active}
                                                    />
                                                </InputAdornment>
                                            }
                                        >
                                            {protocolos.map((p) => {
                                                const id = normalizeId(
                                                    p?.id || p?._id
                                                );
                                                const label =
                                                    p?.nome || p?.name || id;
                                                return (
                                                    <MenuItem
                                                        key={id}
                                                        value={id}
                                                    >
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        disabled={
                                            saving || !normalizeId(protocoloId)
                                        }
                                    >
                                        <InputLabel>Análise</InputLabel>
                                        <Select
                                            label="Análise"
                                            value={analiseId}
                                            onChange={(e) =>
                                                handleChangeAnalise(
                                                    e.target.value
                                                )
                                            }
                                            startAdornment={
                                                <InputAdornment
                                                    position="start"
                                                    sx={{ mr: 1 }}
                                                >
                                                    <Lucide.ClipboardCheck
                                                        size={18}
                                                        color={theme.palette.action.active}
                                                    />
                                                </InputAdornment>
                                            }
                                        >
                                            {analisesDoProtocolo.map((a) => {
                                                const id = normalizeId(
                                                    a?.id || a?._id
                                                );
                                                const label =
                                                    a?.nome || a?.name || id;
                                                return (
                                                    <MenuItem
                                                        key={id}
                                                        value={id}
                                                    >
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                                spacing={isMobile ? 1 : 0}
                            >
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    fontWeight={700}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Lucide.LocateFixed size={18} />{' '}
                                    Localização & Ambiente
                                </Typography>

                                {!isAutoLocation && (
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            width: isMobile ? '100%' : 'auto',
                                            justifyContent: isMobile
                                                ? 'space-between'
                                                : 'flex-end',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() =>
                                                setOpenSearchModal(true)
                                            }
                                            startIcon={<Lucide.Search />}
                                            sx={{ minWidth: 0, px: 2 }}
                                        >
                                            Buscar
                                        </Button>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={requestLocation}
                                                disabled={locationLoading}
                                                startIcon={
                                                    locationLoading ? (
                                                        <CircularProgress
                                                            size={14}
                                                            color="inherit"
                                                        />
                                                    ) : (
                                                        <Lucide.LocateFixed />
                                                    )
                                                }
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                {location
                                                    ? 'Atualizar'
                                                    : 'Usar GPS'}
                                            </Button>
                                            {location && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={clearLocation}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor:
                                                            'error.main',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Lucide.Trash2 size={18} />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Stack>
                                )}
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />

                            {locationError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {locationError}
                                </Alert>
                            )}
                            {locationLoading && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        mb: 1,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Carregando dados de localização...
                                </Typography>
                            )}

                            {location ? (
                                <Box>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ mb: 1.5 }}
                                    >
                                        {isAutoLocation ? (
                                            <Lucide.Building2
                                                color={theme.palette.action.active}
                                                size={18}
                                            />
                                        ) : (
                                            <Lucide.Globe
                                                color={theme.palette.action.active}
                                                size={18}
                                            />
                                        )}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Região de referência:{' '}
                                            <strong>
                                                {displayAddress ||
                                                    'Localização Confirmada'}
                                            </strong>
                                        </Typography>
                                    </Stack>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Temperatura da Amostra (ºC)"
                                        value={ambientTemperature}
                                        onChange={(e) =>
                                            setAmbientTemperature(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Ex: 25.5"
                                        type="text"
                                        disabled={saving}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lucide.Thermometer color={theme.palette.action.active} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            ) : (
                                !locationLoading && (
                                    <Alert
                                        severity="info"
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                    >
                                        Endereço do cliente não encontrado.
                                        Utilize a busca manual ou GPS.
                                    </Alert>
                                )
                            )}
                        </Paper>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <Lucide.FlaskConical size={18} /> Seleção de
                                Experimentos (Mock)
                            </Typography>

                            {!etapaTorresLiberada ? (
                                <Alert
                                    severity="warning"
                                    variant="outlined"
                                    icon={<Lucide.TriangleAlert />}
                                >
                                    Selecione Protocolo e Análise para liberar a
                                    busca de dispositivos.
                                </Alert>
                            ) : (
                                <Stack spacing={2}>
                                    {ruleError && (
                                        <Alert severity="warning">
                                            {ruleError}
                                        </Alert>
                                    )}

                                    <Autocomplete
                                        options={towerOptions}
                                        value={null}
                                        inputValue={towerQuery}
                                        onInputChange={(
                                            event,
                                            newInputValue
                                        ) => {
                                            setTowerQuery(newInputValue);
                                            if (ruleError) setRuleError('');
                                        }}
                                        onChange={(e, tower) => {
                                            if (tower) addTower(tower);
                                        }}
                                        getOptionLabel={(t) =>
                                            `${String(
                                                t?.numeroSerie ?? ''
                                            ).trim()}${
                                                t?.tipo
                                                    ? ` · ${String(
                                                          t.tipo
                                                      ).toUpperCase()}`
                                                    : ''
                                            }`
                                        }
                                        loading={towerLoading}
                                        disabled={saving}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="Buscar Dispositivo para adicionar"
                                                placeholder="Digite o serial..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Lucide.Search />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <>
                                                            {towerLoading ? (
                                                                <CircularProgress
                                                                    size={18}
                                                                />
                                                            ) : null}
                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ ml: 1 }}
                                    >
                                        Busque um dispositivo. Ao selecionar,
                                        carregaremos os experimentos realizados
                                        nele.
                                    </Typography>

                                    <Divider />

                                    {items.length === 0 ? (
                                        <Box
                                            sx={{
                                                py: 3,
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                border: '1px dashed grey',
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Lucide.CirclePlus
                                                size={40}
                                                style={{ opacity: 0.3, marginBottom: 8 }}
                                            />
                                            <Typography variant="body2">
                                                Nenhum item adicionado.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        items.map((x, idx) => {
                                            const t = x.tower;
                                            const towerId = normalizeId(
                                                t?.id || t?._id
                                            );
                                            const serial = String(
                                                t?.numeroSerie ?? ''
                                            ).trim();
                                            const exp = x.experimento;
                                            const expId = normalizeId(exp?.id);
                                            const expStatus = statusChip(
                                                exp?.status
                                            );

                                            return (
                                                <Paper
                                                    key={towerId + idx}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        borderRadius: 2,
                                                        bgcolor: 'action.hover',
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        mb={2}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1}
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    width: 24,
                                                                    height: 24,
                                                                    bgcolor:
                                                                        'primary.main',
                                                                    fontSize:
                                                                        '0.8rem',
                                                                }}
                                                            >
                                                                T
                                                            </Avatar>
                                                            <Typography
                                                                fontWeight={700}
                                                            >
                                                                {serial ||
                                                                    'Sem serial'}
                                                            </Typography>
                                                        </Stack>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                removeTower(
                                                                    towerId
                                                                )
                                                            }
                                                            color="error"
                                                            disabled={saving}
                                                        >
                                                            <Lucide.Trash2 size={18} />
                                                        </IconButton>
                                                    </Stack>

                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={3}
                                                        >
                                                            <DilutionInput
                                                                value={
                                                                    x.diluicao
                                                                }
                                                                onChange={(
                                                                    val
                                                                ) =>
                                                                    updateDiluicao(
                                                                        towerId,
                                                                        val
                                                                    )
                                                                }
                                                                readOnly={
                                                                    saving
                                                                }
                                                            />
                                                        </Grid>

                                                        <Grid
                                                            item
                                                            xs={12}
                                                            md={9}
                                                        >
                                                            {x.expLoading ? (
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                    height="40px"
                                                                >
                                                                    <CircularProgress
                                                                        size={
                                                                            18
                                                                        }
                                                                    />
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                    >
                                                                        Buscando
                                                                        experimentos...
                                                                    </Typography>
                                                                </Stack>
                                                            ) : (
                                                                <Autocomplete
                                                                    options={
                                                                        x.expOptions ||
                                                                        []
                                                                    }
                                                                    value={
                                                                        exp ||
                                                                        null
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                        newExp
                                                                    ) =>
                                                                        updateExperimento(
                                                                            towerId,
                                                                            newExp
                                                                        )
                                                                    }
                                                                    getOptionLabel={(
                                                                        e
                                                                    ) => {
                                                                        const id =
                                                                            normalizeId(
                                                                                e?.id
                                                                            ).slice(
                                                                                -4
                                                                            );
                                                                        const nome =
                                                                            e?.nome ||
                                                                            'S/ Nome';
                                                                        const ini =
                                                                            fmtDateTime(
                                                                                e?.startDate
                                                                            ).split(
                                                                                ' '
                                                                            )[0];
                                                                        return `${nome} (${ini}) [..${id}]`;
                                                                    }}
                                                                    renderOption={(
                                                                        props,
                                                                        option
                                                                    ) => (
                                                                        <li
                                                                            {...props}
                                                                        >
                                                                            <Stack>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    fontWeight={
                                                                                        600
                                                                                    }
                                                                                >
                                                                                    {option.nome ||
                                                                                        'Experimento sem nome'}
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                >
                                                                                    {fmtDateTime(
                                                                                        option.startDate
                                                                                    )}{' '}
                                                                                    •{' '}
                                                                                    {
                                                                                        option.status
                                                                                    }
                                                                                </Typography>
                                                                            </Stack>
                                                                        </li>
                                                                    )}
                                                                    renderInput={(
                                                                        params
                                                                    ) => (
                                                                        <TextField
                                                                            {...params}
                                                                            size="small"
                                                                            label="Vincular Experimento Realizado"
                                                                            placeholder="Selecione um experimento..."
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                startAdornment:
                                                                                    (
                                                                                        <InputAdornment position="start">
                                                                                            <Lucide.Microscope size={18} />
                                                                                        </InputAdornment>
                                                                                    ),
                                                                            }}
                                                                        />
                                                                    )}
                                                                    disabled={
                                                                        saving
                                                                    }
                                                                />
                                                            )}
                                                        </Grid>
                                                    </Grid>

                                                    {expId && (
                                                        <Box
                                                            sx={{
                                                                mt: 2,
                                                                p: 1.5,
                                                                bgcolor:
                                                                    'background.paper',
                                                                borderRadius: 1,
                                                                border: '1px solid',
                                                                borderColor:
                                                                    'divider',
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                mb={1}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    fontWeight={
                                                                        700
                                                                    }
                                                                    color="primary.main"
                                                                >
                                                                    {exp?.nome ||
                                                                        'Experimento Selecionado'}
                                                                </Typography>
                                                                <Chip
                                                                    label={
                                                                        expStatus.label
                                                                    }
                                                                    color={
                                                                        expStatus.color
                                                                    }
                                                                    size="small"
                                                                />
                                                            </Stack>

                                                            <Stack
                                                                direction="row"
                                                                spacing={2}
                                                                flexWrap="wrap"
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    spacing={
                                                                        0.5
                                                                    }
                                                                >
                                                                    <Lucide.CalendarDays
                                                                        size={14}
                                                                        color={theme.palette.text.secondary}
                                                                    />
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {fmtDateTime(
                                                                            exp?.startDate
                                                                        )}
                                                                    </Typography>
                                                                </Stack>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        fontFamily:
                                                                            'monospace',
                                                                    }}
                                                                >
                                                                    ID: {expId}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            );
                                        })
                                    )}
                                </Stack>
                            )}
                        </Paper>
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        flexDirection: isMobile ? 'column-reverse' : 'row',
                        gap: isMobile ? 2 : 1,
                    }}
                >
                    <Button
                        onClick={onClose}
                        disabled={saving}
                        variant="text"
                        color="inherit"
                        size={isMobile ? 'large' : 'small'}
                        fullWidth={isMobile}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        variant="contained"
                        color="secondary"
                        size={isMobile ? 'large' : 'small'}
                        fullWidth={isMobile}
                        startIcon={
                            saving ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <Lucide.Save />
                            )
                        }
                        disableElevation
                        sx={{ px: 4 }}
                    >
                        {saving ? 'Salvando...' : 'Criar Mock'}
                    </Button>
                </DialogActions>
            </Dialog>

            <SearchLocationDialog
                open={openSearchModal}
                onClose={() => setOpenSearchModal(false)}
                onSelect={handleSelectManualLocation}
            />
        </>
    );
};

export default AmostraMockModal;
